import React, { useEffect, useState, useRef } from 'react';
import { useWindowDimensions } from 'react-native';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { meetingActions } from 'src/state/redux/slice/meeting';
import { DNAButton, DNAText } from '@alucio/lux-ui';
import { MeetingType } from '@alucio/aws-beacon-amplify/src/models';
import { useSyncState } from 'src/state/redux/selector/cache'
import workerChannel from 'src/worker/channels/workerChannel'
import format from 'date-fns/format';
import { useAppSettings } from 'src/state/context/AppSettings';
import { contentPreviewModalActions } from 'src/state/redux/slice/contentPreviewModal';
import { useContent, LoadedPresentation } from 'src/state/context/ContentProvider/ContentProvider';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import withPermissionsAccessControl from 'src/hooks/useFeatureFlags/withPermissionsAccessControl';
import { shouldUseHeaderIconButtons } from 'src/components/Header/SearchInput/SearchTypeToggle';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';

enum ContextVariantOptions {
  default,
  contentPreviewModal
}

export interface MeetingButtonProps {
  context?: ContextVariantOption
}

type ContextVariantOption = keyof typeof ContextVariantOptions
type ContextVariants = Record<ContextVariantOption, React.ElementType>

const DefaultMeetingButtonBase: React.FC<{loadedPresentation?: LoadedPresentation}> = (props) => {
  const { loadedPresentation } = props
  const { deviceMode, isPWAStandalone } = useAppSettings();
  const { width } = useWindowDimensions();
  const history = useHistory();
  const dispatch = useDispatch();
  const syncState = useSyncState()
  const isOfflineSyncEnabled = useFeatureFlags('enableOfflineMode');
  const [isMeetingQueued, setIsMeetingQueued] = useState<boolean>(false);
  const isMounted = useRef<boolean>(false)
  const shouldUseIconButton = shouldUseHeaderIconButtons(width);

  useEffect(
    () => {
      isMounted.current = true
      return () => {
        isMounted.current = false
      }
    },
    [],
  )

  const pauseOfflineSyncing = async() => {
    if (!isOfflineSyncEnabled) return;

    // check if service worker is registered
    const registration = await navigator.serviceWorker.getRegistration('/')
    if (registration && syncState?.matches('online.sync')) {
      // pause offline syncing
      workerChannel.postMessageExtended({ type: 'PAUSE_SYNC' })
    }
  }

  const startMeeting = () => {
    pauseOfflineSyncing()
    setIsMeetingQueued(true)

    const onMeetingCreated = (meetingId: string) => {
      if (isMounted.current) {
        setIsMeetingQueued(false)

        if (loadedPresentation) {
          dispatch(contentPreviewModalActions.setInitialContent({
            presentableModelORM: loadedPresentation.presentable.orm,
            page: loadedPresentation.currentPresentablePage.presentationPageNumber,
          }));
          // [NOTE] - Make sure this is done before closing the modal to ensure we skip redirecting from the useCPMDedicatedUrl hook
          // [TODO BEAC-6488] - This probably shouldn't push us into this page until the ORM is in Redux, consider reworking this flow
          history.push(`/meeting-presentation/${meetingId}`)
          dispatch(contentPreviewModalActions.setModalVisibility({ isOpen: false }));
        } else {
          // [TODO BEAC-6488] - This probably shouldn't push us into this page until the ORM is in Redux
          history.push(`/meeting-presentation/${meetingId}`)
        }
      }
    }

    // WE CREATE THE MEETING
    dispatch(meetingActions.startMeeting({
      type: MeetingType.IN_PERSON,
      title: `Meeting Started at ${format(new Date(), 'MM/dd/yyyy h:mm aa')}`,
      callback: onMeetingCreated,
      context: loadedPresentation ? 'PREVIEW_MODAL' : 'TOP_NAV',
    }));
  }

  const button = (
    <DNAButton
      testID={loadedPresentation ? 'header-present-button' : 'header-start-meeting-button'}
      appearance="outline"
      status="tertiary"
      accessibilityLabel={loadedPresentation ? 'Present' : 'Start meeting'}
      onPress={startMeeting}
      disabled={isMeetingQueued}
      {...(
        !loadedPresentation
          ? {
            size: deviceMode === 'desktop' ? 'md' : 'xl',
            iconLeft: shouldUseIconButton ? 'play' : undefined,
            children: shouldUseIconButton ? undefined : 'Start meeting',
          }
          : {
            size: 'lg',
            children: loadedPresentation ? 'Present' : 'Start meeting',
          }
        )
      }
    />
  )

  return loadedPresentation ? button : (
    <DNAPopover disablePopover={!shouldUseIconButton || isPWAStandalone}>
      <DNAPopover.Anchor>
        {button}
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark">Start meeting</DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  )
}

export const DefaultMeetingButton = withPermissionsAccessControl(DefaultMeetingButtonBase, {
  flags: ['enableMeetingFeatures'],
});

/**
 * Context connected version for CPM
 */
export const ContentPreviewModalMeetingButton: React.FC = () => {
  const { activePresentation } = useContent()
  return <DefaultMeetingButton loadedPresentation={activePresentation} />
}

const contextVariants: ContextVariants = {
  default: DefaultMeetingButton,
  contentPreviewModal: ContentPreviewModalMeetingButton,
}

const MeetingButton: React.FC<MeetingButtonProps> = ({ context = 'default' }) => {
  const CurrentContextVariant = contextVariants[context]
  return <CurrentContextVariant />
}

export default MeetingButton;
